import {BasePlugin} from './BasePlugin';
import {CookieConsent} from '../CookieConsent';

/**
 * Checks if the cookie dialog has been blocked by the browser or an extension by checking the buttons are actually
 * visible after opening. Closes the dialog automatically if it detects blocking.
 */
export class DialogBlockDetector extends BasePlugin {
    options = {
        elementsToCheckSelector: 'button[value="yes"], button[value="save_options"]',
        visibilityCheckCallback: element => this.isElementVisible(element),
        blockedMessage: 'Our cookie consent dialog was blocked by your browser or an extension.',
        showAlertOnBlocked: true,
    };

    /**
     * @type {HTMLDialogElement}
     */
    dialog;

    /**
     * @type {?String}
     */
    openingSource = null;

    destroyController = new AbortController();

    constructor(options = {}) {
        super();

        this.options = {
            ...this.options,
            ...options,
        };
    }

    initialize(cookieConsent) {
        super.initialize(cookieConsent);

        this.dialog.addEventListener('open', e => this.onDialogOpen(e), {
            signal: this.destroyController.signal,
        });
    }

    destroy() {
        this.destroyController.abort();
    }

    onDialogOpen(e) {
        this.openingSource = e.detail.source;

        // Wait till the element should have been rendered before running the visibility check. The browser calls the
        // rAF callback when it starts building the next frame (X+1), but *before* it does styleRecalc, layout, etc. Any
        // CSS, class, or attribute changes in the current frame (X) that result in layout changes will not have been
        // applied to the render tree when the rAF callback runs (unless something triggered a sync layout). Running our
        // check in the first rAF callback could thus result in a race condition.
        // By calling our verify method in the nested rAF callback, we skip the first frame and run our checks in the
        // following frame (X+2). At that point, any layout changes will have been applied for the previous frame (X+1).
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                this.verifyElementVisibility();
            });
        });
    }

    verifyElementVisibility() {
        const source = this.openingSource;
        this.openingSource = null;

        // Check that at least one of the elements is visible. This is required because some buttons might be
        // (correctly) hidden depending on the dialog state.
        const elements = this.dialog.querySelectorAll(this.options.elementsToCheckSelector);
        for (const element of elements) {
            if (this.options.visibilityCheckCallback(element)) {
                return;
            }
        }

        console.warn('Cookie consent was automatically closed by `DialogBlockDetector`');
        this.dialog.close();

        if (this.options.showAlertOnBlocked && source === CookieConsent.SOURCE_USER) {
            // eslint-disable-next-line no-alert
            alert(this.options.blockedMessage);
        }
    }

    /**
     * @param {?HTMLElement} element
     *
     * @return {Boolean}
     */
    isElementVisible(element) {
        // The element wasn't part of the DOM.
        if (element === null) {
            return false;
        }

        // Expect an element of at least 5px by 5px.
        return !(element.offsetHeight < 5 || element.offsetWidth < 5);
    }
}
